/**
 * Gate overrides: Admin request, QC co-sign. CR-2026-GATE-OVERRIDE (2026-10-14). Server: /api/gate-overrides.
 *
 * Spec §5.2, §10.2, §17.1: a controlled bypass needs Admin approval and a QC co-sign by a different person.
 * HEAT_ISSUE is single use; FABRICATION_GATE lasts 1 to 72 hours. Never overridable: contract traceability level not
 * recorded (TRC-V01) and grade mismatch (REQ-TR-004). Every step is on record and on the Daily Red Report.
 * Web only; the Kiosk offers no override action.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert, Box, Button, Card, Checkbox, Chip, Container, FormControlLabel, MenuItem, Stack, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import ApiCall from "../../utils/apicall";
import { TYPE_LABEL, STATUS_COLOR, requestProblem, requestBody, canCosign } from "./overrideModel";

import SmartTable from "../../components/SmartTable/SmartTable";
const msgOf = (e, f) => e?.response?.data?.message || e?.message || f;
const when = (d) => (d ? String(d).replace("T", " ").slice(0, 16) : "");

function RequestForm({ onDone, initial }) {
  const [f, setF] = useState({ type: initial.type || "HEAT_ISSUE", jobId: initial.jobId || "", heatId: initial.heatId || "", pieceIds: [], itemIds: [], reason: "", hours: 24 });
  const [jobs, setJobs] = useState([]); const [heats, setHeats] = useState([]); const [marks, setMarks] = useState([]);
  const [itemId, setItemId] = useState(""); const [pieces, setPieces] = useState([]);
  const [msg, setMsg] = useState(null); const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => { ApiCall.get("/job").then((r) => setJobs(r.data?.data?.jobs || r.data?.data || [])).catch(() => setJobs([])); }, []);
  useEffect(() => {
    setHeats([]); setMarks([]); setPieces([]); setItemId("");
    if (!f.jobId) return;
    ApiCall.get(`/heat/job/${f.jobId}`).then((r) => setHeats((r.data?.data?.heats || []).filter((h) => ["QUARANTINE", "AT_RISK"].includes(String(h.status).toUpperCase())))).catch(() => setHeats([]));
    ApiCall.get(`/fabricated-items/job/${f.jobId}`).then((r) => setMarks(Array.isArray(r.data?.data) ? r.data.data : [])).catch(() => setMarks([]));
  }, [f.jobId]);
  useEffect(() => {
    setPieces([]);
    if (!itemId) return;
    ApiCall.get(`/piece-trace/items/${itemId}/pieces`).then((r) => setPieces((r.data.data || []).filter((p) => p.status === "ACTIVE"))).catch(() => setPieces([]));
  }, [itemId]);

  const togglePiece = (id) => set("pieceIds", f.pieceIds.includes(id) ? f.pieceIds.filter((x) => x !== id) : [...f.pieceIds, id]);
  const toggleItem = (id) => set("itemIds", f.itemIds.includes(id) ? f.itemIds.filter((x) => x !== id) : [...f.itemIds, id]);
  const submit = async () => {
    const p = requestProblem(f); if (p) { setMsg({ severity: "error", text: p }); return; }
    setBusy(true); setMsg(null);
    try { const r = await ApiCall.post("/gate-overrides", requestBody(f)); setMsg({ severity: "success", text: r.data.message }); setF((s) => ({ ...s, pieceIds: [], itemIds: [], reason: "" })); onDone(); }
    catch (e) { setMsg({ severity: "error", text: msgOf(e, "Not requested.") }); } finally { setBusy(false); }
  };

  return (
    <Card sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6">Request an override</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        A QC co-signer (Quality Manager or QCI, not you) must sign before it applies. Contract level not recorded and grade
        mismatch are never overridable.
      </Typography>
      {msg && <Alert severity={msg.severity} sx={{ mb: 1.5 }}>{msg.text}</Alert>}
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 1.5 }}>
        <TextField select size="small" label="Type" value={f.type} onChange={(e) => setF((s) => ({ ...s, type: e.target.value, pieceIds: [], itemIds: [] }))} sx={{ minWidth: 300 }}>
          {Object.entries(TYPE_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Job" value={f.jobId} onChange={(e) => setF((s) => ({ ...s, jobId: e.target.value, heatId: "", pieceIds: [], itemIds: [] }))} sx={{ minWidth: 260 }}>
          <MenuItem value="">Choose a job</MenuItem>
          {jobs.map((j) => <MenuItem key={j.id} value={String(j.id)}>{j.name}</MenuItem>)}
        </TextField>
        {f.type === "HEAT_ISSUE" && (
          <TextField select size="small" label="Heat (quarantined or at risk)" value={f.heatId} onChange={(e) => set("heatId", e.target.value)} sx={{ minWidth: 240 }}>
            <MenuItem value="">{heats.length ? "Choose a heat" : "No quarantined or at-risk heat"}</MenuItem>
            {heats.map((h) => <MenuItem key={h.id} value={String(h.id)}>{h.heatNumber} ({h.status})</MenuItem>)}
          </TextField>
        )}
        <TextField size="small" type="number" label="Window (hours)" value={f.hours} onChange={(e) => set("hours", e.target.value)} inputProps={{ min: 1, max: 72 }} sx={{ width: 140 }} />
      </Stack>
      {f.type === "CLOSEOUT" ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>Closes the job once without its final quality data package. The reason prints on the Daily Red Report; QC must co-sign.</Alert>
      ) : f.type === "CONTRACT_REVIEW" ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>Lets the job past the F1.6.0 contract review gate (CR-V01) for the window: release, package activation, material issue and station work. Every pass is counted and prints on the Daily Red Report; QC must co-sign.</Alert>
      ) : f.type === "REOPEN" ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>Moves a completed job back to open work once. Closing it again needs its final package (a new revision) or a closeout override.</Alert>
      ) : f.type === "HEAT_ISSUE" ? (
        <Box sx={{ mb: 1.5 }}>
          <TextField select size="small" label="Mark" value={itemId} onChange={(e) => setItemId(e.target.value)} sx={{ minWidth: 260 }}>
            <MenuItem value="">Choose a mark</MenuItem>
            {marks.map((m) => <MenuItem key={m.id} value={m.id}>{m.name} (qty {m.quantity})</MenuItem>)}
          </TextField>
          <Box sx={{ mt: 1 }}>
            {pieces.map((p) => (
              <FormControlLabel key={p.id} label={`${p.mark} ${p.display}`}
                control={<Checkbox size="small" checked={f.pieceIds.includes(p.id)} onChange={() => togglePiece(p.id)} />} />
            ))}
          </Box>
          <Typography variant="caption">{f.pieceIds.length} piece{f.pieceIds.length === 1 ? "" : "s"} selected (max 50)</Typography>
        </Box>
      ) : (
        <Box sx={{ mb: 1.5 }}>
          {marks.map((m) => (
            <FormControlLabel key={m.id} label={`${m.name} (qty ${m.quantity})`}
              control={<Checkbox size="small" checked={f.itemIds.includes(m.id)} onChange={() => toggleItem(m.id)} />} />
          ))}
          <Typography variant="caption" display="block">{f.itemIds.length} mark{f.itemIds.length === 1 ? "" : "s"} selected (max 50)</Typography>
        </Box>
      )}
      <TextField fullWidth multiline minRows={2} size="small" label="Reason (prints on the Daily Red Report)" value={f.reason} onChange={(e) => set("reason", e.target.value)} />
      <Button variant="contained" color="warning" sx={{ mt: 1.5 }} disabled={busy} onClick={submit}>Request override</Button>
    </Card>
  );
}

export default function GateOverrides() {
  const [params] = useSearchParams();
  const { loginUser } = useSelector((s) => s.userSlice);
  const perms = loginUser?.decodedToken?.permissions || [];
  const userId = loginUser?.decodedToken?.id;
  const canRequest = perms.includes("request_gate_override");
  const canQc = perms.includes("cosign_gate_override");
  const [status, setStatus] = useState("");
  const [list, setList] = useState([]);
  const [msg, setMsg] = useState(null);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await ApiCall.get(`/gate-overrides${status ? `?status=${status}` : ""}`); setList(r.data.data || []); }
    catch (e) { setMsg({ severity: "error", text: msgOf(e, "Could not load overrides.") }); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const act = async (o, verb) => {
    const text = String(notes[o.id] || "").trim();
    if (verb === "reject" && text.length < 4) { setMsg({ severity: "error", text: "Give the reason for rejecting or withdrawing (at least 4 characters) in the note box." }); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await ApiCall.post(`/gate-overrides/${o.id}/${verb}`, verb === "cosign" ? { note: text || undefined } : { reason: text });
      setMsg({ severity: "success", text: r.data.message }); setNotes((n) => ({ ...n, [o.id]: "" })); await load();
    } catch (e) { setMsg({ severity: "error", text: msgOf(e, "Not recorded.") }); } finally { setBusy(false); }
  };

  return (
    <>
      <Helmet><title>Gate overrides | SFM</title></Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4">Gate overrides</Typography>
            <Typography variant="body2" color="text.secondary">Admin request, QC co-sign by a different person. Every override is on the Daily Red Report.</Typography>
          </Box>
          <Button component={RouterLink} to="/red-report" variant="outlined">Daily Red Report</Button>
        </Stack>
        {canRequest && <RequestForm onDone={load} initial={{ type: params.get("type"), jobId: params.get("jobId"), heatId: params.get("heatId") }} />}
        {msg && <Alert severity={msg.severity} sx={{ mb: 1.5 }}>{msg.text}</Alert>}
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 200, mb: 1.5 }}>
          <MenuItem value="">All</MenuItem>
          {Object.keys(STATUS_COLOR).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <Card>
          <SmartTable tableId="gate-overrides" lockedColumns={["#", "Status", "Type"]} size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell><TableCell>Status</TableCell><TableCell>Type</TableCell><TableCell>Job</TableCell><TableCell>Scope</TableCell>
                <TableCell>Reason</TableCell><TableCell>Requested</TableCell><TableCell>QC</TableCell><TableCell>Window</TableCell><TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length === 0 && <TableRow><TableCell colSpan={10}><Typography variant="caption" color="text.secondary">No overrides.</Typography></TableCell></TableRow>}
              {list.map((o) => {
                const cos = canCosign(o, userId, perms);
                const own = o.status === "REQUESTED" && Number(o.requestedBy) === Number(userId);
                const canReject = (canQc || canRequest) && ["REQUESTED", "COSIGNED"].includes(o.status);
                return (
                  <TableRow key={o.id} hover>
                    <TableCell>{o.id}</TableCell>
                    <TableCell><Chip size="small" color={STATUS_COLOR[o.status] || "default"} label={o.status} /></TableCell>
                    <TableCell>{o.type === "HEAT_ISSUE" ? "Heat issue" : o.type === "CLOSEOUT" ? "Job closeout" : o.type === "REOPEN" ? "Job reopen" : o.type === "CONTRACT_REVIEW" ? "Contract review" : "Fabrication gate"}</TableCell>
                    <TableCell>{o.jobName || o.jobId}</TableCell>
                    <TableCell>{o.type === "HEAT_ISSUE" ? `Heat ${o.heatNumber || o.heatId}, ${o.pieceIds.length} piece(s)` : ["CLOSEOUT", "REOPEN", "CONTRACT_REVIEW"].includes(o.type) ? "Whole job" : `${o.itemIds.length} mark(s)`}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>{o.reason}{o.rejectReason ? ` | Rejected: ${o.rejectReason}` : ""}</TableCell>
                    <TableCell>{o.requestedByName}<br /><Typography variant="caption">{when(o.requestedAt)}</Typography></TableCell>
                    <TableCell>{o.cosignedByName || o.rejectedByName || ""}<br /><Typography variant="caption">{when(o.cosignedAt || o.rejectedAt)}{o.cosignNote ? ` ${o.cosignNote}` : ""}</Typography></TableCell>
                    <TableCell>{o.hours} h{o.expiresAt ? <><br /><Typography variant="caption">until {when(o.expiresAt)}</Typography></> : null}{o.useCount ? <><br /><Typography variant="caption">used {o.useCount}x</Typography></> : null}</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      {(cos || canReject) && (
                        <Stack spacing={0.75}>
                          <TextField size="small" placeholder={cos ? "QC note or reject reason" : "Reason to reject or withdraw"} value={notes[o.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [o.id]: e.target.value }))} />
                          <Stack direction="row" spacing={0.75}>
                            {cos && <Button size="small" variant="contained" disabled={busy} onClick={() => act(o, "cosign")}>Co-sign</Button>}
                            {canReject && <Button size="small" color="error" disabled={busy} onClick={() => act(o, "reject")}>{o.status === "COSIGNED" ? "Withdraw" : "Reject"}</Button>}
                          </Stack>
                        </Stack>
                      )}
                      {own && canQc && <Typography variant="caption" color="text.secondary" display="block">Your request: another QC person co-signs.</Typography>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </SmartTable>
        </Card>
      </Container>
    </>
  );
}
